import { createFileRoute, useRouter } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { buildActivityAssigneeOptions } from '@apps/web/components/home/activity_assignment';
import {
    LocalSavedViewsBar,
    localSavedViewsStorageKey,
    useLocalSavedViews,
} from '@apps/web/components/saved_views/local_saved_views';
import { PhoebeFlowerIcon } from '@apps/web/components/settings/phoebe_flower_icon';
import { ContactActivityPanel } from '@apps/web/components/shared/messages/contact_activity_panel';
import {
    type ConversationInboxView,
    type ConversationOfficeFilter,
    type ConversationQueueView,
    type ConversationReadFilter,
    type ConversationStatusFilter,
} from '@apps/web/components/shared/messages/conversation_list_panel_header';
import { compareConversationMetadata } from '@apps/web/components/shared/messages/conversation_list_utils';
import {
    OrganizationConversationDetailPane,
    OrganizationConversationListPanel,
} from '@apps/web/components/shared/messages/organization_conversation_panels';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Sheet, SheetContent } from '@apps/web/components/ui/sheet';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import {
    useOrganizationConversationRealtime,
    useOrganizationConversationsRealtime,
    useOrganizationPendingMessagesCountRealtime,
    useOrganizationUnreadCountRealtime,
} from '@apps/web/hooks/realtime_hooks';
import {
    UNASSIGNED_CARE_COORDINATOR_ID,
    useCareCoordinatorFilter,
} from '@apps/web/hooks/use_care_coordinator_filter';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import {
    localFilterMemoryStorageKey,
    useLocalFilterMemory,
} from '@apps/web/hooks/use_local_filter_memory';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useResourceOrganizationLookup } from '@apps/web/hooks/use_resource_organization_lookup';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    generateExternalCaregiverUrl,
    getDataSourceDisplayName,
} from '@apps/web/lib/data_source_utils';
import { EVENTS } from '@apps/web/lib/events';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useMode } from '@apps/web/lib/mode_store';
import { cn } from '@apps/web/lib/utils';
import { useActivityEventAssignees } from '@apps/web/swr_data_fetching/activity_event_assignments';
import { useConversationOwnershipActions } from '@apps/web/swr_data_fetching/conversation_ownership';
import {
    useBlockedCaregivers,
    useConversationActions,
    useOrganization,
    useOrganizationCaregiver,
    useOrganizationConversationOfficeCounts,
    useOrganizationConversations,
    useOrganizationDataSourceAccounts,
    useOrganizationOffices,
    useUser,
    useUserProfile,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { ApiError } from '@libraries/typescript/api_client';

// Persists the Contact Activity Timeline panel's open/closed state across
// sessions. Org-independent: the preference is a UI layout choice, not
// org-scoped data.
const ACTIVITY_PANEL_STORAGE_KEY = 'phoebe.conversations.activityPanelOpen';

const SEARCH_DEBOUNCE_DELAY_MS = 300;
const EMPTY_OFFICE_IDS: ReadonlyArray<string> = [];
const COMPACT_FILTER_CLASS_NAME =
    'h-7 shrink-0 gap-1 rounded-md px-2 text-xs font-medium shadow-none has-[>svg]:px-2';

export const conversationSavedViewPayloadSchema = z
    .object({
        // Older saved views used tabs for unread and pending filtering. Accept
        // that field long enough to migrate those views into the dedicated
        // controls without preserving invisible tab state.
        activeTab: z.enum(['all', 'unread', 'pending']).optional(),
        queueView: z.enum(['me', 'unassigned', 'all']),
        statusFilter: z.enum(['open', 'closed', 'all']),
        readFilter: z.enum(['unread', 'read']).nullable(),
        pendingOnly: z.boolean().default(false),
        pendingFirst: z.boolean(),
        showHidden: z.boolean(),
        inboxView: z.enum(['list', 'board']),
    })
    .transform(({ activeTab, ...payload }) => ({
        ...payload,
        readFilter:
            payload.readFilter ??
            (activeTab === 'unread' ? ('unread' as const) : null),
        pendingOnly: payload.pendingOnly || activeTab === 'pending',
    }));

export type ConversationSavedViewPayload = z.infer<
    typeof conversationSavedViewPayloadSchema
>;

const conversationFilterMemorySchema = z.object({
    version: z.literal(1),
    filters: conversationSavedViewPayloadSchema,
    officeIds: z.array(z.string()),
    includeUnassignedOffice: z.boolean(),
});

export type ConversationFilterMemory = z.infer<
    typeof conversationFilterMemorySchema
>;

export function conversationSavedViewPayloadsEqual(
    left: ConversationSavedViewPayload,
    right: ConversationSavedViewPayload,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

export const Route = createFileRoute('/_app/(main)/conversations/')({
    head: () => ({
        meta: [
            {
                title: 'Conversations | Phoebe',
            },
        ],
    }),
    component: ConversationsPage,
    validateSearch: z.object({
        conversationId: z.string().optional(),
        messageId: z.string().optional(),
        // Deep-link entry point (e.g. from the activity board): the page
        // resolves the caregiver's most recent conversation and replaces the
        // URL with a concrete `conversationId`.
        caregiverId: z.string().optional(),
        organizationId: z.string().optional(),
        mode: z.enum(['live', 'sandbox']).optional(),
        inboxView: z.enum(['list', 'board']).optional(),
    }),
});

function ConversationsPage() {
    const {
        conversationId,
        messageId,
        caregiverId,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
        inboxView,
    } = Route.useSearch();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );

    if (isWaitingForDeepLinkOrganization) {
        return null;
    }

    return (
        <ConversationsPageContent
            conversationId={conversationId}
            messageId={messageId}
            caregiverId={caregiverId}
            inboxView={inboxView ?? 'list'}
        />
    );
}

function ConversationsPageContent({
    conversationId,
    messageId,
    caregiverId: caregiverIdParam,
    inboxView,
}: {
    readonly conversationId: string | undefined;
    readonly messageId: string | undefined;
    readonly caregiverId: string | undefined;
    readonly inboxView: ConversationInboxView;
}) {
    const router = useRouter();
    const posthog = usePostHog();
    const organizationId = useOrganizationId();
    const mode = useMode() ?? 'live';
    const { data: organization } = useOrganization(organizationId, {
        reason: 'conversations-ownership-flag',
    });
    const careCoordinatorSupportEnabled =
        !!organization?.features?.care_coordinator_support_enabled;
    const ccFilter = useCareCoordinatorFilter({
        enabled: careCoordinatorSupportEnabled,
        localStorageKey: careCoordinatorSupportEnabled
            ? 'conversations:care-coordinator-filter'
            : undefined,
    });
    const careCoordinatorFilterLabel = (() => {
        if (ccFilter.isShowingAll) {
            return 'All';
        }

        const careCoordinators = ccFilter.careCoordinators ?? [];
        const selectedCoordinators = careCoordinators.filter((coordinator) =>
            ccFilter.isCoordinatorSelected(coordinator.id),
        );
        const isUnassignedSelected = ccFilter.isCoordinatorSelected(
            UNASSIGNED_CARE_COORDINATOR_ID,
        );
        const selectedCount =
            selectedCoordinators.length + (isUnassignedSelected ? 1 : 0);

        if (selectedCount === 1) {
            if (isUnassignedSelected) {
                return 'No Coordinator';
            }
            return ccFilter.formatCoordinatorName(selectedCoordinators[0]!);
        }
        if (selectedCount > 1) {
            return `${selectedCount} selected`;
        }
        return 'All';
    })();
    const careCoordinatorFilter =
        careCoordinatorSupportEnabled &&
        (ccFilter.careCoordinators?.length ?? 0) > 0 ? (
            <DropdownMenu
                onOpenChange={ccFilter.setIsOpen}
                open={ccFilter.isOpen}
            >
                <DropdownMenuTrigger asChild={true}>
                    <Button
                        aria-label={`Coordinator: ${careCoordinatorFilterLabel}`}
                        className={COMPACT_FILTER_CLASS_NAME}
                        size="sm"
                        type="button"
                        variant="outline"
                    >
                        <span className="max-w-52 truncate">
                            Coordinator: {careCoordinatorFilterLabel}
                        </span>
                        <ChevronDown className="size-3" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuItem onSelect={ccFilter.handleShowAll}>
                        All Care Coordinators
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                        checked={ccFilter.isCoordinatorSelected(
                            UNASSIGNED_CARE_COORDINATOR_ID,
                        )}
                        onCheckedChange={() =>
                            ccFilter.handleSelect(
                                UNASSIGNED_CARE_COORDINATOR_ID,
                            )
                        }
                        onSelect={(event) => event.preventDefault()}
                    >
                        No Care Coordinator
                    </DropdownMenuCheckboxItem>
                    {ccFilter.careCoordinators?.map((coordinator) => (
                        <DropdownMenuCheckboxItem
                            checked={ccFilter.isCoordinatorSelected(
                                coordinator.id,
                            )}
                            key={coordinator.id}
                            onCheckedChange={() =>
                                ccFilter.handleSelect(coordinator.id)
                            }
                            onSelect={(event) => event.preventDefault()}
                        >
                            <span className="min-w-0 flex-1 truncate">
                                {ccFilter.formatCoordinatorName(coordinator)}
                            </span>
                        </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        ) : null;
    const ownershipEnabled = useFeatureFlag(
        'conversation_ownership_enabled',
        organization?.features,
    );
    const isOwnershipFlagLoading = organization === undefined;
    const { data: currentUser } = useUser({ reason: 'conversations-index' });
    const handleInboxViewChange = useCallback(
        (view: ConversationInboxView) => {
            router.navigate({
                to: '/conversations',
                search: (previous) => ({
                    ...(previous as {
                        conversationId?: string;
                        organizationId?: string;
                        mode?: 'live' | 'sandbox';
                    }),
                    inboxView: view,
                }),
                replace: true,
            });
        },
        [router],
    );
    const [queueView, setQueueView] = useState<ConversationQueueView>('all');
    const [statusFilter, setStatusFilter] =
        useState<ConversationStatusFilter>('all');
    // The inbox opens with no read filter across all lifecycle statuses. A
    // default Unread filter rendered as a dismissible chip the user never
    // applied, and it hid every conversation from users with nothing unread —
    // including their search results. Unread stays one click away. The read and
    // lifecycle filters are independent.
    const [readFilter, setReadFilter] = useState<ConversationReadFilter | null>(
        null,
    );
    // Ownership gates the board and the open/closed lifecycle, and the board is
    // deliberately limited to open work. The query, the saved-view payload, and
    // the panel props all read the result rather than re-deriving it, so a
    // status left over from a saved view or an earlier session cannot scope the
    // list for an org that has no visible control to clear it.
    const effectiveInboxView = ownershipEnabled ? inboxView : 'list';
    const showBoard = effectiveInboxView === 'board';
    const effectiveStatusFilter: ConversationStatusFilter = !ownershipEnabled
        ? 'all'
        : showBoard
          ? 'open'
          : statusFilter;
    const isMobile = useIsMobile();
    const { timezone } = useOrganizationTimezone();
    const [activityPanelOpen, setActivityPanelOpen] = useState<boolean>(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return localStorage.getItem(ACTIVITY_PANEL_STORAGE_KEY) === 'true';
    });
    const handleToggleActivityPanel = useCallback(() => {
        setActivityPanelOpen((open) => {
            const next = !open;
            if (typeof window !== 'undefined') {
                localStorage.setItem(
                    ACTIVITY_PANEL_STORAGE_KEY,
                    next ? 'true' : 'false',
                );
            }
            posthog.capture(EVENTS.CONTACT_ACTIVITY_PANEL_TOGGLED, {
                open: next,
            });
            return next;
        });
    }, [posthog]);
    const [search, setSearch] = useState('');
    // Server-side search (name, number, and message content) — debounce so
    // each keystroke doesn't trigger a new API request.
    const debouncedSearch = useDebounce(
        search.trim(),
        SEARCH_DEBOUNCE_DELAY_MS,
    );
    const [pendingFirst, setPendingFirst] = useState(false);
    const [pendingOnly, setPendingOnly] = useState(false);
    const pendingSelectionRequestedRef = useRef(false);
    const [showHidden, setShowHidden] = useState(false);
    const [activeSavedViewId, setActiveSavedViewId] = useState<string | null>(
        null,
    );
    const savedViewStorageKey =
        currentUser?.id && organizationId
            ? localSavedViewsStorageKey(
                  'conversations',
                  organizationId,
                  currentUser.id,
              )
            : null;
    const savedViews = useLocalSavedViews({
        storageKey: savedViewStorageKey,
        payloadSchema: conversationSavedViewPayloadSchema,
    });
    // Search text and the selected conversation are temporary. A saved view
    // stores the repeatable inbox filters as they are actually in effect.
    const currentSavedViewPayload = useMemo(
        () => ({
            queueView,
            statusFilter: effectiveStatusFilter,
            readFilter,
            pendingOnly,
            pendingFirst,
            showHidden,
            inboxView: effectiveInboxView,
        }),
        [
            effectiveInboxView,
            effectiveStatusFilter,
            pendingOnly,
            pendingFirst,
            queueView,
            readFilter,
            showHidden,
        ],
    );
    const { data: userProfile } = useUserProfile({
        reason: 'conversations-office-scope',
    });
    const { data: offices } = useOrganizationOffices(organizationId, {
        reason: 'conversations-office-scope',
    });
    const { data: officeConversationCounts } =
        useOrganizationConversationOfficeCounts(organizationId, {
            reason: 'conversations-office-scope',
        });
    const assignedOfficeIds = useMemo(() => {
        const profile = userProfile?.org_member_profiles.find(
            (candidate) => candidate.organization_id === organizationId,
        );
        return profile?.assigned_office_ids ?? [];
    }, [userProfile?.org_member_profiles, organizationId]);
    const [officeFilter, setOfficeFilter] =
        useState<ConversationOfficeFilter | null>(null);
    const selectedOfficeIds = officeFilter?.officeIds ?? EMPTY_OFFICE_IDS;
    const includeUnassignedOffice = officeFilter?.includeUnassigned ?? false;
    const selectedOfficeIdsKey = useMemo(
        () => [...selectedOfficeIds].sort().join(','),
        [selectedOfficeIds],
    );
    const careCoordinatorIdsKey = useMemo(
        () => [...(ccFilter.filterIds ?? [])].sort().join(','),
        [ccFilter.filterIds],
    );
    const filterMemoryStorageKey =
        currentUser?.id && organizationId
            ? localFilterMemoryStorageKey(
                  'conversations',
                  organizationId,
                  currentUser.id,
                  mode,
              )
            : null;
    const filterMemoryValue = useMemo<ConversationFilterMemory>(
        () => ({
            version: 1,
            filters: currentSavedViewPayload,
            officeIds: [...selectedOfficeIds],
            includeUnassignedOffice,
        }),
        [currentSavedViewPayload, includeUnassignedOffice, selectedOfficeIds],
    );
    const { isHydrated: isFilterMemoryHydrated } = useLocalFilterMemory({
        storageKey: filterMemoryStorageKey,
        schema: conversationFilterMemorySchema,
        value: filterMemoryValue,
        enabled: userProfile !== undefined,
        defaultValue: (): ConversationFilterMemory => ({
            version: 1,
            filters: {
                queueView: 'all',
                statusFilter: 'all',
                readFilter: null,
                pendingOnly: false,
                pendingFirst:
                    localStorage.getItem(
                        `phoebe:conversations:${organizationId}:pending-first`,
                    ) === 'true',
                showHidden: false,
                inboxView,
            },
            officeIds: assignedOfficeIds,
            includeUnassignedOffice:
                localStorage.getItem(
                    `phoebe:conversations:${organizationId}:include-unassigned-office`,
                ) === 'true',
        }),
        onRestore: (memory) => {
            setQueueView(memory.filters.queueView);
            setStatusFilter(memory.filters.statusFilter);
            setReadFilter(memory.filters.readFilter);
            setPendingOnly(memory.filters.pendingOnly);
            setPendingFirst(memory.filters.pendingFirst);
            setShowHidden(memory.filters.showHidden);
            if (memory.filters.inboxView !== inboxView) {
                handleInboxViewChange(memory.filters.inboxView);
            }
            setOfficeFilter({
                officeIds: memory.officeIds,
                includeUnassigned: memory.includeUnassignedOffice,
            });
            setActiveSavedViewId(null);
        },
    });
    // Don't fetch the inbox until the remembered scope is resolved. Otherwise
    // an org switch could briefly query every office before restoring it.
    const officeScopeReady =
        userProfile !== undefined &&
        officeFilter !== null &&
        isFilterMemoryHydrated;

    // Assignment and the open/closed lifecycle both belong to the ownership
    // rollout; read state is a list filter for every organization.
    const assigneeParam = ownershipEnabled
        ? queueView === 'me'
            ? 'me'
            : queueView === 'unassigned'
              ? 'unassigned'
              : undefined
        : undefined;
    const statusParam =
        effectiveStatusFilter === 'all' ? undefined : effectiveStatusFilter;
    const effectiveReadState = readFilter ?? undefined;
    const {
        conversations,
        isLoading,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        mutate: mutateConversationPages,
    } = useOrganizationConversationsRealtime({
        search: debouncedSearch,
        sort: pendingFirst ? 'pending_first' : undefined,
        includeHidden: showHidden,
        hasPendingMessages: pendingOnly ? true : undefined,
        readState: effectiveReadState,
        officeIds: selectedOfficeIds.length > 0 ? selectedOfficeIds : undefined,
        includeUnassignedOffice,
        careCoordinatorIds: careCoordinatorSupportEnabled
            ? ccFilter.filterIds
            : undefined,
        includeUnassignedCareCoordinators: careCoordinatorSupportEnabled
            ? ccFilter.includeUnassigned
            : undefined,
        status: statusParam,
        assignee: assigneeParam,
        // The board fetches the first page of open conversations at a larger
        // page size so grouping into columns has enough rows to be useful.
        pageSize: showBoard ? 200 : undefined,
        enabled: officeScopeReady,
    });
    const { data: unreadCount, mutate: mutateUnreadCount } =
        useOrganizationUnreadCountRealtime('conversations-index');
    const { data: pendingMessagesCount } =
        useOrganizationPendingMessagesCountRealtime(
            'conversations-bubble-pending-count',
        );
    const {
        conversation: activeConversation,
        error: activeConversationError,
        mutate: mutateActiveConversationPages,
    } = useOrganizationConversationRealtime(conversationId ?? null, {
        autoUpdate: true,
        markAsRead: false,
    });
    const isMissingConversationInCurrentOrganization =
        activeConversationError instanceof ApiError &&
        activeConversationError.status === 404;
    const { isResolvingAcrossOrganizations } = useResourceOrganizationLookup({
        enabled: !!conversationId && isMissingConversationInCurrentOrganization,
        resourceId: conversationId,
        resourceType: 'conversation',
    });

    const conversationsWithLiveSelection = useMemo(() => {
        if (!conversationId || !activeConversation) {
            return conversations;
        }

        const hasSelectedConversation = conversations.some(
            (conversation) => conversation.id === conversationId,
        );
        if (!hasSelectedConversation) {
            return conversations;
        }

        const nextConversations = conversations.map((conversation) =>
            conversation.id === conversationId
                ? {
                      ...activeConversation,
                      // The list cache is the source of truth for read state.
                      unread_count: conversation.unread_count,
                  }
                : conversation,
        );

        if (!debouncedSearch) {
            nextConversations.sort((a, b) =>
                compareConversationMetadata(
                    a,
                    b,
                    pendingFirst ? 'pending_first' : undefined,
                ),
            );
        }

        return nextConversations;
    }, [
        activeConversation,
        conversationId,
        conversations,
        debouncedSearch,
        pendingFirst,
    ]);
    const handlePendingOnlyToggle = useCallback(() => {
        setPendingOnly((current) => {
            const next = !current;
            pendingSelectionRequestedRef.current = next;
            return next;
        });
    }, []);
    useEffect(() => {
        if (!pendingOnly || !pendingSelectionRequestedRef.current) {
            return;
        }

        const firstPendingConversation = conversationsWithLiveSelection.find(
            (conversation) => conversation.pending_staged_message_count > 0,
        );
        if (!firstPendingConversation) {
            if (!isLoading && conversationsWithLiveSelection.length === 0) {
                pendingSelectionRequestedRef.current = false;
            }
            return;
        }

        pendingSelectionRequestedRef.current = false;
        if (firstPendingConversation.id === conversationId) {
            return;
        }

        router.navigate({
            to: '/conversations',
            search: { conversationId: firstPendingConversation.id },
            replace: true,
        });
    }, [
        conversationId,
        conversationsWithLiveSelection,
        isLoading,
        pendingOnly,
        router,
    ]);
    const selectedConversation =
        conversationsWithLiveSelection.find((c) => c.id === conversationId) ??
        activeConversation ??
        undefined;
    const caregiverId = selectedConversation?.caregiver_id ?? null;
    const { data: caregiver } = useOrganizationCaregiver(
        caregiverId ? organizationId : null,
        caregiverId,
        { reason: 'conversations-index-ehr-link' },
    );
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        caregiverId ? organizationId : null,
        { reason: 'conversations-index-ehr-link' },
    );
    const caregiverEhrLink = useMemo(() => {
        if (!caregiver?.external_id || !caregiver?.source_system) {
            return null;
        }

        const dataSourceAccount = dataSourceAccounts?.find((account) => {
            return account.type === caregiver.source_system;
        });
        if (!dataSourceAccount) {
            return null;
        }

        return {
            href: generateExternalCaregiverUrl(
                caregiver.source_system,
                caregiver.external_id,
                dataSourceAccount.base_url,
            ),
            label: `View in ${getDataSourceDisplayName(caregiver.source_system)}`,
        };
    }, [caregiver?.external_id, caregiver?.source_system, dataSourceAccounts]);

    const mostRecentConversationId = conversationsWithLiveSelection[0]?.id;

    // Caregiver deep-link resolution (e.g. from the activity board): when a
    // `caregiverId` is present in the URL and no concrete conversation is
    // selected yet, fetch that caregiver's conversations and select the most
    // recent one. The backend orders by `last_active_at DESC`, so element [0]
    // is the newest.
    const shouldResolveCaregiver = !!caregiverIdParam && !conversationId;
    const {
        conversations: caregiverConversations,
        isLoading: isLoadingCaregiverConversations,
    } = useOrganizationConversations(
        shouldResolveCaregiver ? organizationId : null,
        { reason: 'conversations-caregiver-deep-link' },
        undefined,
        {
            caregiverId: caregiverIdParam,
            enabled: shouldResolveCaregiver,
        },
    );

    // Get blocked caregiver IDs for conversations
    const caregiverIdsForBlockedCheck = useMemo(() => {
        const ids = conversationsWithLiveSelection
            .map((c) => c.caregiver_id)
            .filter((id): id is string => !!id);
        return ids.length > 0 ? ids : null;
    }, [conversationsWithLiveSelection]);

    const { data: blockedCaregivers, mutate: mutateBlockedCaregivers } =
        useBlockedCaregivers(organizationId, caregiverIdsForBlockedCheck);

    const { markAsRead, markAsUnread, hide, unhide } = useConversationActions(
        organizationId,
        [mutateConversationPages],
        mutateUnreadCount,
    );
    const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);

    // Conversation ownership: assignee options for the board columns and the
    // assign/close/reopen mutations. Only fetched/used when the flag is on.
    const { data: assigneesData } = useActivityEventAssignees(
        ownershipEnabled ? organizationId : null,
        organizationId ? [organizationId] : [],
    );
    const boardAssignees = useMemo(
        () => buildActivityAssigneeOptions(assigneesData?.users ?? []),
        [assigneesData?.users],
    );
    const {
        assign: assignConversation,
        close: closeConversationAction,
        reopen: reopenConversationAction,
    } = useConversationOwnershipActions(
        organizationId,
        mutateConversationPages,
        mutateActiveConversationPages,
    );

    const handleAssignConversation = useCallback(
        (conversationId: string, assignedToUserId: string | null) => {
            posthog?.capture(EVENTS.CONVERSATION_ASSIGNED, {
                conversation_id: conversationId,
                assigned_to_user_id: assignedToUserId,
                source: 'detail_pane',
            });
            void assignConversation(conversationId, assignedToUserId).catch(
                () =>
                    toast.error('Failed to update assignment', {
                        persistNotification: false,
                    }),
            );
        },
        [assignConversation, posthog],
    );

    const handleReassignFromBoard = useCallback(
        (conversationId: string, assignedToUserId: string | null) => {
            posthog?.capture(EVENTS.CONVERSATION_CARD_DRAG_REASSIGNED, {
                conversation_id: conversationId,
                assigned_to_user_id: assignedToUserId,
            });
            void assignConversation(conversationId, assignedToUserId).catch(
                () =>
                    toast.error('Failed to update assignment', {
                        persistNotification: false,
                    }),
            );
        },
        [assignConversation, posthog],
    );

    const handleCloseConversation = useCallback(
        (conversationId: string) => {
            posthog?.capture(EVENTS.CONVERSATION_CLOSED, {
                conversation_id: conversationId,
            });
            void closeConversationAction(conversationId)
                .then(() =>
                    toast.success('Conversation closed', {
                        persistNotification: false,
                    }),
                )
                .catch(() =>
                    toast.error('Failed to close conversation', {
                        persistNotification: false,
                    }),
                );
        },
        [closeConversationAction, posthog],
    );

    const handleReopenConversation = useCallback(
        (conversationId: string) => {
            posthog?.capture(EVENTS.CONVERSATION_REOPENED, {
                conversation_id: conversationId,
            });
            void reopenConversationAction(conversationId)
                .then(() =>
                    toast.success('Conversation reopened', {
                        persistNotification: false,
                    }),
                )
                .catch(() =>
                    toast.error('Failed to reopen conversation', {
                        persistNotification: false,
                    }),
                );
        },
        [reopenConversationAction, posthog],
    );

    const applySavedView = useCallback(
        (payload: ConversationSavedViewPayload) => {
            // Stored state goes in raw; the effective-filter derivation above
            // decides what ownership and the board make of it.
            setQueueView(payload.queueView);
            setStatusFilter(payload.statusFilter);
            setReadFilter(payload.readFilter);
            setPendingOnly(payload.pendingOnly);
            setPendingFirst(payload.pendingFirst);
            setShowHidden(payload.showHidden);
            setSearch('');
            handleInboxViewChange(
                ownershipEnabled ? payload.inboxView : 'list',
            );
        },
        [handleInboxViewChange, ownershipEnabled],
    );

    useEffect(() => {
        setActiveSavedViewId(null);
    }, [organizationId]);

    const handleConversationMessageCreated = useCallback(
        async (newConversationId: string) => {
            await mutateConversationPages();
            void mutateUnreadCount();
            router.navigate({
                to: '/conversations',
                search: {
                    conversationId: newConversationId,
                },
            });
        },
        [mutateConversationPages, mutateUnreadCount, router],
    );

    const getBlockedInfosForCaregiver = useMemo(
        () => (caregiverId: string | null | undefined) =>
            caregiverId
                ? (blockedCaregivers?.filter(
                      (b) => b.caregiver_id === caregiverId,
                  ) ?? [])
                : [],
        [blockedCaregivers],
    );

    const loadMoreConversations = useCallback(() => {
        if (isLoadingMore || isReachingEnd) {
            return;
        }

        void setSize(size + 1);
    }, [isLoadingMore, isReachingEnd, setSize, size]);
    const handleMarkAllAsRead = useCallback(async () => {
        if (!organizationId || isMarkingAllAsRead || (unreadCount ?? 0) === 0) {
            return;
        }

        setIsMarkingAllAsRead(true);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/conversations/mark-all-read',
                pathParams: {
                    organization_id: organizationId,
                },
            });

            const updatedIds = new Set(response.updated_conversation_ids ?? []);

            await mutateConversationPages(
                (pages) => {
                    if (!pages || updatedIds.size === 0) {
                        return pages;
                    }

                    let didChange = false;

                    const nextPages = pages.map((page) => {
                        let pageChanged = false;

                        const nextConversations = page.conversations.map(
                            (conversation) => {
                                if (!updatedIds.has(conversation.id)) {
                                    return conversation;
                                }

                                pageChanged = true;
                                didChange = true;

                                return {
                                    ...conversation,
                                    unread_count: 0,
                                };
                            },
                        );

                        return pageChanged
                            ? {
                                  ...page,
                                  conversations: nextConversations,
                              }
                            : page;
                    });

                    return didChange ? nextPages : pages;
                },
                { revalidate: false },
            );

            if (updatedIds.size > 0) {
                void mutateConversationPages();
            }
            void mutateUnreadCount();

            toast.success('Marked all conversations as read', {
                persistNotification: true,
                notificationType: 'message_action',
            });
        } catch (error) {
            console.error('Failed to mark all conversations as read', error);
            toast.error('Failed to mark all conversations as read', {
                persistNotification: true,
                notificationType: 'message_action',
            });
        } finally {
            setIsMarkingAllAsRead(false);
        }
    }, [
        isMarkingAllAsRead,
        mutateConversationPages,
        mutateUnreadCount,
        organizationId,
        unreadCount,
    ]);

    // Reset pagination when filters or organization change. useSWRInfinite resets
    // the cache automatically when the key changes, but `size` (the number of
    // pages loaded) persists across key changes, so we need to reset it to 1.
    // Only call setSize when size > 1 to avoid a redundant refetch.
    const previousFiltersRef = useRef({
        organizationId,
        search: debouncedSearch,
        pendingOnly,
        pendingFirst,
        showHidden,
        selectedOfficeIdsKey,
        includeUnassignedOffice,
        careCoordinatorIdsKey,
        includeUnassignedCareCoordinators: ccFilter.includeUnassigned,
        queueView,
        statusFilter: effectiveStatusFilter,
        readFilter,
    });
    useEffect(() => {
        const prev = previousFiltersRef.current;
        previousFiltersRef.current = {
            organizationId,
            search: debouncedSearch,
            pendingOnly,
            pendingFirst,
            showHidden,
            selectedOfficeIdsKey,
            includeUnassignedOffice,
            careCoordinatorIdsKey,
            includeUnassignedCareCoordinators: ccFilter.includeUnassigned,
            queueView,
            statusFilter: effectiveStatusFilter,
            readFilter,
        };

        if (
            prev.organizationId === organizationId &&
            prev.search === debouncedSearch &&
            prev.pendingOnly === pendingOnly &&
            prev.pendingFirst === pendingFirst &&
            prev.showHidden === showHidden &&
            prev.selectedOfficeIdsKey === selectedOfficeIdsKey &&
            prev.includeUnassignedOffice === includeUnassignedOffice &&
            prev.careCoordinatorIdsKey === careCoordinatorIdsKey &&
            prev.includeUnassignedCareCoordinators ===
                ccFilter.includeUnassigned &&
            prev.queueView === queueView &&
            prev.statusFilter === effectiveStatusFilter &&
            prev.readFilter === readFilter
        ) {
            return;
        }

        if (size > 1) {
            void setSize(1);
        }
    }, [
        organizationId,
        debouncedSearch,
        pendingOnly,
        pendingFirst,
        showHidden,
        selectedOfficeIdsKey,
        includeUnassignedOffice,
        careCoordinatorIdsKey,
        ccFilter.includeUnassigned,
        queueView,
        effectiveStatusFilter,
        readFilter,
        size,
        setSize,
    ]);

    useEffect(() => {
        // Resolve a caregiver deep-link to their most recent conversation and
        // replace the URL with the concrete `conversationId`. `replace: true`
        // keeps the back button pointed at the referrer (e.g. the activity
        // board), not this intermediate caregiver URL.
        if (shouldResolveCaregiver && !isLoadingCaregiverConversations) {
            const first = caregiverConversations[0];
            if (first) {
                router.navigate({
                    to: '/conversations',
                    search: { conversationId: first.id },
                    replace: true,
                });
            }
            // Empty result (caregiver has no conversation): intentionally do
            // nothing so the existing empty-detail state renders, rather than
            // bouncing the user back to /activity.
        }
    }, [
        caregiverConversations,
        isLoadingCaregiverConversations,
        router,
        shouldResolveCaregiver,
    ]);

    useEffect(() => {
        // On mobile, leave the URL alone when no conversation is selected —
        // the user is meant to see the list view. Auto-selecting a thread
        // would defeat the back button and trap the user in a thread.
        if (
            conversationId ||
            !mostRecentConversationId ||
            isMobile ||
            isOwnershipFlagLoading ||
            showBoard ||
            // While a caregiver deep-link is resolving, let that effect pick the
            // caregiver's newest conversation instead of the full inbox's.
            shouldResolveCaregiver
        ) {
            return;
        }

        router.navigate({
            to: '/conversations',
            search: {
                conversationId: mostRecentConversationId,
            },
            replace: true,
        });
    }, [
        conversationId,
        isMobile,
        isOwnershipFlagLoading,
        mostRecentConversationId,
        router,
        showBoard,
        shouldResolveCaregiver,
    ]);

    if (!organizationId) {
        return (
            <div className="flex h-full items-center justify-center">
                <p className="text-muted-foreground">No organization found</p>
            </div>
        );
    }

    if (isResolvingAcrossOrganizations) {
        return null;
    }

    // On viewports below `md`, only one pane is visible at a time: the list
    // when no conversation is selected, and the thread once one is. Above
    // `md`, both panes render side-by-side as before.
    const isThreadOpen = !!conversationId;

    return (
        <div
            className="bg-background flex h-full min-h-0 w-full overflow-hidden rounded-lg border"
            data-testid="conversations-workspace"
        >
            <div
                data-testid="conversation-list-shell"
                className={cn(
                    'h-full min-h-0 w-full flex-col overflow-hidden border-r md:flex',
                    // The board needs the full width; the classic list stays a
                    // fixed-width sidebar beside the thread pane.
                    showBoard ? 'flex md:max-w-none' : 'md:max-w-sm',
                    !showBoard && isThreadOpen ? 'hidden md:flex' : 'flex',
                    !showBoard ? 'bg-background' : null,
                )}
            >
                <OrganizationConversationListPanel
                    conversations={conversationsWithLiveSelection}
                    getBlockedInfosForCaregiver={getBlockedInfosForCaregiver}
                    hasMore={!isReachingEnd}
                    isLoading={isLoading}
                    isLoadingMore={isLoadingMore}
                    isMarkingAllAsRead={isMarkingAllAsRead}
                    markAsRead={markAsRead}
                    markAsUnread={markAsUnread}
                    organizationId={organizationId}
                    onConversationMessageCreated={
                        handleConversationMessageCreated
                    }
                    onLoadMore={loadMoreConversations}
                    onMarkAllAsRead={handleMarkAllAsRead}
                    onSelectConversation={(selectedConversationId) => {
                        router.navigate({
                            to: '/conversations',
                            search: {
                                conversationId: selectedConversationId,
                            },
                        });
                    }}
                    onUnblocked={mutateBlockedCaregivers}
                    hide={hide}
                    unhide={unhide}
                    pendingFirst={pendingFirst}
                    search={search}
                    selectedConversationId={conversationId}
                    setPendingFirst={setPendingFirst}
                    setSearch={setSearch}
                    showHidden={showHidden}
                    setShowHidden={setShowHidden}
                    totalPendingCount={pendingMessagesCount ?? 0}
                    totalUnreadCount={unreadCount ?? 0}
                    officeOptions={offices ?? []}
                    officeFilter={officeFilter ?? undefined}
                    onOfficeFilterChange={setOfficeFilter}
                    officeConversationCounts={officeConversationCounts?.offices}
                    unassignedOfficeCount={
                        officeConversationCounts?.unassigned_count
                    }
                    ownershipEnabled={ownershipEnabled}
                    queueView={queueView}
                    setQueueView={setQueueView}
                    statusFilter={effectiveStatusFilter}
                    setStatusFilter={setStatusFilter}
                    inboxView={effectiveInboxView}
                    setInboxView={handleInboxViewChange}
                    readFilter={readFilter}
                    setReadFilter={setReadFilter}
                    careCoordinatorFilter={careCoordinatorFilter}
                    currentUserId={currentUser?.id ?? null}
                    boardAssignees={boardAssignees}
                    onReassignConversation={handleReassignFromBoard}
                    onCloseConversation={handleCloseConversation}
                    onReopenConversation={handleReopenConversation}
                    savedViews={
                        savedViewStorageKey ? (
                            <LocalSavedViewsBar
                                views={savedViews.views}
                                currentPayload={currentSavedViewPayload}
                                activeViewId={activeSavedViewId}
                                matchesPayload={
                                    conversationSavedViewPayloadsEqual
                                }
                                onApplyView={(view) =>
                                    applySavedView(view.payload)
                                }
                                onActiveViewChange={setActiveSavedViewId}
                                onCreateView={savedViews.createView}
                                onUpdateView={savedViews.updateView}
                                onRenameView={savedViews.renameView}
                                onDeleteView={savedViews.deleteView}
                                label="Saved view"
                                navigationLabel="Conversation saved views"
                                trailingContent={
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={
                                            pendingOnly
                                                ? 'secondary'
                                                : 'outline'
                                        }
                                        aria-pressed={pendingOnly}
                                        className={cn(
                                            'h-8 shrink-0 rounded-full px-3 text-xs shadow-none',
                                            pendingOnly
                                                ? 'bg-agent-action hover:bg-agent-action-strong focus-visible:ring-agent-action/35 font-semibold text-white'
                                                : 'border-agent-action/30 bg-background text-agent-action hover:border-agent-action/50 hover:bg-agent-action/10 hover:text-agent-action',
                                        )}
                                        onClick={handlePendingOnlyToggle}
                                    >
                                        <PhoebeFlowerIcon
                                            className={cn(
                                                'size-4 shrink-0',
                                                pendingOnly
                                                    ? 'text-white'
                                                    : 'text-agent-action',
                                            )}
                                        />
                                        Pending review
                                    </Button>
                                }
                            />
                        ) : null
                    }
                />
            </div>
            <div
                data-testid="conversation-thread-shell"
                className={cn(
                    'h-full min-h-0 w-full flex-col overflow-hidden md:flex',
                    // Hide the thread pane entirely while the board owns the
                    // full width; selecting a card drops back to the list view.
                    showBoard
                        ? 'hidden'
                        : isThreadOpen
                          ? 'flex'
                          : 'hidden md:flex',
                )}
            >
                <OrganizationConversationDetailPane
                    conversationId={conversationId ?? null}
                    highlightMessageId={messageId ?? null}
                    externalLinkHref={caregiverEhrLink?.href ?? null}
                    externalLinkLabel={caregiverEhrLink?.label ?? null}
                    organizationId={organizationId}
                    markAsRead={markAsRead}
                    markAsUnread={markAsUnread}
                    onBack={
                        isMobile
                            ? () => {
                                  router.navigate({
                                      to: '/conversations',
                                      search: {},
                                  });
                              }
                            : undefined
                    }
                    selectedConversation={selectedConversation}
                    ownershipEnabled={ownershipEnabled}
                    onAssignConversation={handleAssignConversation}
                    onCloseConversation={handleCloseConversation}
                    onReopenConversation={handleReopenConversation}
                    activityPanelOpen={activityPanelOpen}
                    onToggleActivityPanel={handleToggleActivityPanel}
                />
            </div>
            {ownershipEnabled &&
            activityPanelOpen &&
            conversationId &&
            !showBoard ? (
                isMobile ? (
                    <Sheet
                        open={true}
                        onOpenChange={(open) => {
                            if (!open) {
                                handleToggleActivityPanel();
                            }
                        }}
                    >
                        <SheetContent
                            className="w-full max-w-sm p-0"
                            side="right"
                        >
                            <ContactActivityPanel
                                organizationId={organizationId}
                                conversationId={conversationId}
                                contactName={
                                    selectedConversation?.caregiver_name ??
                                    selectedConversation?.external_contact_name ??
                                    null
                                }
                                timeZone={timezone}
                                onClose={handleToggleActivityPanel}
                            />
                        </SheetContent>
                    </Sheet>
                ) : (
                    <div
                        data-testid="conversation-activity-shell"
                        className="hidden h-full min-h-0 w-96 shrink-0 flex-col overflow-hidden border-l md:flex"
                    >
                        <ContactActivityPanel
                            organizationId={organizationId}
                            conversationId={conversationId}
                            contactName={
                                selectedConversation?.caregiver_name ??
                                selectedConversation?.external_contact_name ??
                                null
                            }
                            timeZone={timezone}
                            onClose={handleToggleActivityPanel}
                        />
                    </div>
                )
            ) : null}
        </div>
    );
}
